import { Link } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { motion } from "framer-motion";
import { ArrowRight, TreePine, Shield, Heart, Users } from "lucide-react";
import { Button } from "@/components/ui/button";
import { IMAGES, SITE_INFO } from "@/lib/constants";

const fadeUp = {
  initial: { opacity: 0, y: 30 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true, margin: "-80px" },
  transition: { duration: 0.6 },
};

const values = [
  { icon: TreePine, title: "Nature First", desc: "We preserve and celebrate the natural beauty of Lake Texoma in everything we do." },
  { icon: Shield, title: "Safety & Security", desc: "Our gated campus ensures a safe, private experience for all guests." },
  { icon: Heart, title: "Warm Hospitality", desc: "We treat every guest like family, going above and beyond for memorable stays." },
  { icon: Users, title: "Community", desc: "Building connections between guests, nature, and the local Lake Texoma community." },
];

export default function AboutPage() {
  return (
    <>
      <Helmet>
        <title>About Texoma Resorts | Serenity Village Near Lake Texoma</title>
        <meta name="description" content="Learn about Texoma Resorts and Serenity Village. A luxury nature retreat near Lake Texoma offering glamping, cabins, camping, and adventure experiences in Denison, Texas." />
      </Helmet>

      <section className="relative h-[50vh] min-h-[400px] flex items-end">
        <div className="absolute inset-0">
          <img src={IMAGES.sunset} alt="Lake Texoma sunset" className="w-full h-full object-cover" />
          <div className="hero-overlay absolute inset-0" />
        </div>
        <div className="relative z-10 w-full max-w-7xl mx-auto px-6 md:px-12 pb-12">
          <motion.div initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }}>
            <p className="caption-text text-white/60 mb-3">Our Story</p>
            <h1 className="font-heading text-4xl sm:text-5xl lg:text-7xl font-light text-white tracking-tight">
              About Texoma Resorts
            </h1>
          </motion.div>
        </div>
      </section>

      <section className="py-16 md:py-24">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <motion.div {...fadeUp}>
              <p className="caption-text text-accent mb-3">Serenity Village</p>
              <h2 className="font-heading text-3xl md:text-4xl font-normal text-foreground mb-6">
                Where Nature Meets Luxury
              </h2>
              <p className="font-body text-base text-muted-foreground leading-relaxed mb-4">
                Texoma Resorts, home to Serenity Village, is a gated luxury glamping and
                camping resort located adjacent to Lake Texoma and Eisenhower State Park
                in Denison, Texas.
              </p>
              <p className="font-body text-base text-muted-foreground leading-relaxed mb-4">
                We created Serenity Village to offer a unique escape from everyday life.
                Our resort combines the beauty of the Texas outdoors with the comforts
                of luxury hospitality, giving guests the best of both worlds.
              </p>
              <p className="font-body text-base text-muted-foreground leading-relaxed mb-8">
                From our signature Bet Cabin and glamping safari tents to geodesic domes
                and traditional camping sites, we offer accommodations for every type of
                traveler. Add on ATV adventures, speed boat tours, and romantic packages
                for a truly unforgettable experience.
              </p>
              <Link to="/accommodations">
                <Button data-testid="about-explore" className="bg-primary hover:bg-primary/90 text-white font-body rounded-full px-6">
                  Explore Our Stays <ArrowRight size={16} className="ml-2" />
                </Button>
              </Link>
            </motion.div>
            <motion.div {...fadeUp} transition={{ ...fadeUp.transition, delay: 0.2 }}>
              <div className="grid grid-cols-2 gap-3">
                <div className="img-hover-zoom rounded-lg overflow-hidden aspect-square">
                  <img src={IMAGES.homeDetail1} alt="Resort grounds" className="w-full h-full object-cover" />
                </div>
                <div className="img-hover-zoom rounded-lg overflow-hidden aspect-square">
                  <img src={IMAGES.homeDetail2} alt="Lakeside views" className="w-full h-full object-cover" />
                </div>
                <div className="col-span-2 img-hover-zoom rounded-lg overflow-hidden aspect-video">
                  <img src={IMAGES.homeDetail4} alt="Resort detail" className="w-full h-full object-cover" />
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      <section className="py-16 md:py-24 bg-white">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <motion.div {...fadeUp} className="text-center mb-12">
            <p className="caption-text text-accent mb-3">What We Stand For</p>
            <h2 className="font-heading text-3xl md:text-5xl font-light text-foreground tracking-tight">Our Values</h2>
          </motion.div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-8">
            {values.map((v, i) => (
              <motion.div key={v.title} {...fadeUp} transition={{ ...fadeUp.transition, delay: i * 0.1 }}>
                <div className="text-center">
                  <div className="w-14 h-14 mx-auto mb-4 rounded-full bg-primary/5 flex items-center justify-center">
                    <v.icon size={24} className="text-primary" />
                  </div>
                  <h3 className="font-heading text-lg font-semibold text-foreground mb-2">{v.title}</h3>
                  <p className="font-body text-sm text-muted-foreground">{v.desc}</p>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      <section className="py-16 md:py-24 bg-secondary/30">
        <div className="max-w-3xl mx-auto px-6 md:px-12 text-center">
          <motion.div {...fadeUp}>
            <h2 className="font-heading text-3xl md:text-5xl font-light text-foreground tracking-tight mb-4">
              Come Visit Us
            </h2>
            <p className="font-body text-base text-muted-foreground mb-2">{SITE_INFO.address}</p>
            <p className="font-body text-base text-muted-foreground mb-8">{SITE_INFO.phone}</p>
            <Link to="/contact">
              <Button data-testid="about-contact" className="bg-accent hover:bg-accent/90 text-white font-body rounded-full px-8">
                Get in Touch <ArrowRight size={16} className="ml-2" />
              </Button>
            </Link>
          </motion.div>
        </div>
      </section>
    </>
  );
}
